import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cart from '../src/assets/cart.svg';
import Pagination from './Pagination';
import axios from 'axios';

const Products = ({ selectedType, teemka, counting, types, cartItems, setCartItems, quantities, setQuantities }) => {
  const [limitedProducts, setLimitedProducts] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const [outOfStockItems, setOutOfStockItems] = useState({}); // Track out-of-stock messages
  const [successMessage, setSuccessMessage] = useState(false); // Track success message
  const itemsPerPage = 12;

  useEffect(() => {
    setLimitedProducts(types.slice(0, itemsPerPage));
    triggerAnimation();
  }, [types]);

  function triggerAnimation() {
    setIsMounted(false);
    setTimeout(() => {
      setIsMounted(true);
    }, []);
  }

  const handlePageChange = (start, end) => {
    const limitedProducts1 = types.slice(start, end);
    setLimitedProducts(limitedProducts1);
    triggerAnimation();
    window.scrollTo(0, 0);
  };

  const handleQuantityChange = (id, value) => {
    const newQuantity = Math.max(1, Math.min(20, parseInt(value) || 1));
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [id]: newQuantity,
    }));
  };
  
  const handleItemSelection = async (id) => {
    try {
      const stockResponse = await axios.get(`https://cottonlocalhost1365.onrender.com/api/items/${id}`);
      const availableStock = stockResponse.data.quantity;
  
      const currentQuantityInCart = cartItems.find((item) => item.productId === id)?.quantity || 0;
      const desiredQuantity = quantities?.[id] || 1;
      const totalQuantity = currentQuantityInCart + desiredQuantity;
  
      if (totalQuantity > availableStock) {
        const remainingStock = availableStock - currentQuantityInCart;
        const message =
          remainingStock < 1
            ? 'Out of stock'
            : `Only ${remainingStock} items left in stock.`;
        setOutOfStockItems((prev) => ({
          ...prev,
          [id]: message,
        }));
        setTimeout(() => {
          setOutOfStockItems((prev) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }, 10000);
        return;
      }
  
      // Call the teemka function (passed from App.js) to add the item to the cart
      teemka(id);
  
      setSuccessMessage(true);
      setTimeout(() => {
        setSuccessMessage(false);
      }, 2000);
      counting();
    } catch (error) {
      console.error('Error fetching stock or adding item to cart:', error);
    }
  };

  const getHeading = () => {
    switch (selectedType) {
      case 'men':
        return "Men's Socks";
      case 'women':
        return "Women's Socks";
      case 'box':
        return "Box of Socks";
      case 'kids':
        return "Kid's Socks";
      case 'onesize':
        return "Onesize";
      default:
        return "All Products";
    }
  };

  return (
    <section
      className={`text-[#333333] w-full min-h-screen flex flex-col items-center opacity-0 ${
        isMounted ? 'animate-fade-in' : 'invisible'
      }`}
      style={{ animationDelay: `${1 * 0.2}s` }}
    >
      {/* Section Heading */}
      <h2 className="p-6 text-3xl font-bold text-[#1A2A3A] mb-8">{getHeading()}</h2>

      {/* Product Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 px-4 w-full max-w-7xl">
        {limitedProducts.map((sock) => {
          const isOutOfStock = outOfStockItems[sock._id]?.includes('Out of stock') || outOfStockItems[sock._id]?.includes('Only');
          return (
            <div
              key={sock._id}
              className="flex flex-col items-center bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out transform hover:-translate-y-2 hover:scale-105"
            >
              {/* Product Image */}
              <Link to={`/products/${sock._id}`} className="w-full">
                <img
                  src={sock.name}
                  alt="Socks of Cotton Kosova Shop"
                  className="w-full h-48 md:h-56 object-cover rounded-t-xl"
                />
              </Link>

              {/* Product Details */}
              <div className="p-4 w-full">
                <p className="text-sm text-gray-600 text-center mb-2">{sock.description}</p>
                <p className="text-lg font-semibold text-[#1A2A3A] text-center mb-4">
                  {sock.price} €
                </p>

                {/* Out of Stock Message */}
                {outOfStockItems[sock._id] && (
                  <p className="text-red-500 text-sm text-center mb-2">{outOfStockItems[sock._id]}</p>
                )}

                {/* Quantity Input with Buttons */}
                {!isOutOfStock && (
                  <div className="flex items-center justify-center mb-4">
                    <button
                      onClick={() => handleQuantityChange(sock._id, Math.max(1, (quantities[sock._id] || 1) - 1))}
                      className="bg-gray-200 text-gray-700 w-8 h-8 flex items-center justify-center rounded-l-lg hover:bg-gray-300 transition-all duration-200"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      id={`quantity-${sock._id}`}
                      name={`quantity-${sock._id}`}
                      min="1"
                      max="20"
                      value={quantities[sock._id] || 1}
                      onChange={(e) => handleQuantityChange(sock._id, e.target.value)}
                      className="w-16 px-2 py-1 border-t border-b border-gray-300 text-sm text-center"
                    />
                    <button
                      onClick={() => handleQuantityChange(sock._id, Math.min(20, (quantities[sock._id] || 1) + 1))}
                      className="bg-gray-200 text-gray-700 w-8 h-8 flex items-center justify-center rounded-r-lg hover:bg-gray-300 transition-all duration-200"
                    >
                      +
                    </button>
                  </div>
                )}

                {/* Add to Cart Button */}
                {!isOutOfStock && (
                  <button
                    onClick={() => handleItemSelection(sock._id)}
                    className="w-full flex items-center justify-center bg-[#1A2A3A] text-white py-2 rounded-lg hover:bg-indigo-600 transition-all duration-300"
                  >
                    <img src={cart} alt="cart icon" className="w-5 h-5 mr-2" />
                    <span>Add to Cart</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center items-center my-12 w-2/3 max-w-7xl">
        <div className="flex justify-center items-center bg-[#1A2A3A] w-full rounded-xl p-4 shadow-lg">
          <Pagination
            totalItems={types.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </section>
  );
};

export default Products;